import { Link } from 'foundations-components/transitional/components/Link';
import { Select } from 'foundations-components/transitional/components/Select';
import { TypeaheadResultsItem } from 'foundations-components/transitional/components/Typeahead';
import { syntheticEvent as SyntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { usePrevious } from '../../hooks/usePrevious';
import { requestIdleCallback } from '../../utils/requestIdleCallback';
import { SearchBarInput } from './SearchBarInput';
import { jsx as _jsx } from "react/jsx-runtime";
const NoopExternalIcon = () => null;
export default function SearchBar({
  inputValue,
  isUngatedForUnifiedStorefront,
  onClear,
  onInputValueChange,
  onOpenChange,
  onResultClick,
  onSearchSubmit,
  open: initialOpen = false,
  use = 'default',
  options,
  isLoading,
  placeholder,
  readOnly,
  onSelectedOptionChange,
  loadOptions
}) {
  const [searchInputValue, setSearchInputValue] = useState(inputValue);
  const [clearButtonClicked, setClearButtonClicked] = useState(false);
  const [open, setOpen] = useState(initialOpen);
  const prevInitialOpen = usePrevious(initialOpen);
  const isLink = use === 'link';
  const isExternalLink = use === 'external-link';
  const renderOption = useCallback(option => {
    if (option.href && (isLink || isExternalLink || option.seeAllButton)) {
      const {
        href,
        linkUse,
        dropdownText,
        text
      } = option;
      return /*#__PURE__*/_jsx(TypeaheadResultsItem, {
        _useButton: false,
        message: true,
        option: Object.assign({}, option, {
          dropdownText: /*#__PURE__*/_jsx(Link, {
            paddingBlock: "100",
            paddingInline: "300",
            ExternalIcon: NoopExternalIcon,
            external: isExternalLink,
            href: href,
            onMouseDown: evt => {
              evt.stopPropagation();
              if (onResultClick) {
                onResultClick(option);
              }
              if (isExternalLink) {
                requestIdleCallback(() => setOpen(false), 500);
              }
            },
            use: linkUse,
            children: dropdownText || text
          })
        })
      });
    }
    return /*#__PURE__*/_jsx(TypeaheadResultsItem, {
      message: true,
      option: option
    });
  }, [isExternalLink, isLink, onResultClick]);
  const handleOpenChange = useCallback(({
    target: {
      value
    }
  }) => {
    if (clearButtonClicked) {
      setClearButtonClicked(false);
      return;
    }
    if (onOpenChange) {
      onOpenChange(value);
    }
    setOpen(value);
  }, [clearButtonClicked, onOpenChange]);
  const handleSubmit = useCallback(value => {
    setOpen(false);
    if (onSearchSubmit) {
      onSearchSubmit(value);
    }
  }, [setOpen, onSearchSubmit]);
  const handleInputValueChange = useCallback(e => {
    const value = e.target.value;
    setSearchInputValue(value);
    if (!value.trim()) {
      if (onClear) {
        onClear();
      }
      // Create a SyntheticEvent with the correct type
      if (onInputValueChange) {
        const syntheticEvent = Object.create(SyntheticEvent.prototype, {
          target: {
            value: {
              value: ''
            }
          },
          type: {
            value: 'change'
          }
        });
        onInputValueChange(syntheticEvent);
      }
    } else if (onInputValueChange) {
      onInputValueChange(e);
    }

    // Check if e is an instance of SyntheticEvent
    if (e && e instanceof SyntheticEvent) {
      setClearButtonClicked(true);
    }
  }, [onClear, onInputValueChange]);
  const handleInputKeyDown = useCallback(e => {
    const {
      key,
      target: {
        value: _value
      }
    } = e;
    const value = _value.trim();
    if (value && key === 'Enter') {
      e.preventDefault();
      handleSubmit(value);
    }
  }, [handleSubmit]);
  useEffect(() => {
    if (initialOpen !== prevInitialOpen) {
      setOpen(initialOpen);
    }
  }, [initialOpen, prevInitialOpen]);

  // forwardRef so the ref the Select forwards to `inputComponent` doesn't hit a
  // plain function component (React 18 "Function components cannot be given refs"
  // warning; dropped silently under React 19). Forward it via SearchBarInput's
  // `inputRef` prop, which it passes through to SearchInput's underlying input,
  // so the Select can focus the search input. useMemo keeps the component
  // identity stable so the Select doesn't remount it.
  const inputComponent = useMemo(() => /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(SearchBarInput, Object.assign({}, props, {
    isUngatedForUnifiedStorefront: isUngatedForUnifiedStorefront,
    inputRef: ref
  }))), [isUngatedForUnifiedStorefront]);
  return /*#__PURE__*/_jsx(Select, {
    anchorType: "input",
    cache: false,
    "data-test-id": "storefront-search-bar",
    filterOptions: false,
    inputComponent: inputComponent,
    inputValue: searchInputValue,
    isLoading: isLoading,
    loadOptions: loadOptions,
    maxBlockSize: "400px",
    onInputKeyDown: handleInputKeyDown,
    onInputValueChange: handleInputValueChange,
    onOpenChange: handleOpenChange,
    onSelectedOptionChange: onSelectedOptionChange,
    open: open,
    optionRenderer: renderOption,
    options: options,
    placeholder: placeholder,
    readOnly: readOnly,
    value: null
  });
}